import { ToastType } from '@components/Toast/Toast';
import {
  deleteLoadComment,
  enableTracking,
  fetchOrderDocuments,
  postLoadComment,
  putLoadCommentVisibility,
} from '@store/orders/services/commentsDocumentsTrackingServices';
import {
  orderStore,
  setDocuments,
  setDocumentsLoading,
  setOrderStore,
} from '@store/orders/store';
import { ILoadComments, OrderState } from '@store/orders/types';
import { Comments } from '@typeDefinitions/comments';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import {
  ILoadEditViewModel,
  TrackingDataPayload,
} from '@typeDefinitions/loadTypes';
import { logError } from '@utils/errorLogging';
import { handleToast } from '@utils/utils';
import { TrackingCommentSchema } from '@views/order/components/customerTracking/CustomerTracking';
import { produce } from 'solid-js/store';

import { applyOrderHydration } from './orderFormActions';
import { defaultLoadTrackingRequest } from '../defaultVals';

export const removeComment = async (
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  await deleteLoadComment(commentId, changeTrackingSource);
};

export const changeCommentVisibility = async (
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  await putLoadCommentVisibility(commentId, changeTrackingSource);
};

export const fetchDocuments = async (orderId: string) => {
  try {
    const documents = await fetchOrderDocuments(orderId);
    setDocuments(documents);
  } catch (error) {
    handleToast(ToastType.Error, (error as Error).message);
  } finally {
    setDocumentsLoading(false);
  }
};

export const sendComment = async (
  comment: TrackingCommentSchema,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response = await postLoadComment(comment, changeTrackingSource);

    const commentNew: ILoadComments = {
      city: response.city,
      comment: response.comment,
      id: response.id,
      name: response.name,
      state: response.state,
      temperature: response.temperature,
      timestamp: response.timestamp,
      visibleToCustomer: response.visibleToCustomer,
      latitude: Number(comment.latitude),
      longitude: Number(comment.longitude),
      milesRemaining: response.milesRemaining,
      objectId: response.objectId,
      operationType: response.operationType,
      thumbnail: response.thumbnail,
      type: response.type,
      userId: response.userId,
      unitOfTemperature: null,
    };

    applyOrderHydration(() => {
      setOrderStore(
        'order',
        'loads',
        (load: ILoadEditViewModel) => load.id === comment.loadId,
        'comments',
        (comments) => [commentNew, ...(comments || [])],
      );
    });
  } catch (error) {
    logError('sendComment', error as Error);
    handleToast(ToastType.Error, (error as Error).message);
  }
};

export const addCommentInOrderStore = (comment: Comments) => {
  setOrderStore((prev: OrderState) => {
    const date = new Date(comment.timestamp!);
    const formattedDate = date.toISOString().replace('Z', '');
    comment.timestamp = formattedDate;
    return {
      ...prev,
      order: {
        ...prev.order,
        comments: [...(prev.order.comments ?? []), comment],
      },
    };
  });
};

export const deleteCommentInOrderStore = (commentId: number) => {
  setOrderStore((prev: OrderState) => {
    return {
      ...prev,
      order: {
        ...prev.order,
        comments: prev.order.comments?.filter((c) => c.id !== commentId),
      },
    };
  });
};

export const enableLoadTracking = async (trackingData: TrackingDataPayload) => {
  const response = await enableTracking(trackingData);

  if (Boolean(response?.success)) {
    setOrderStore(
      produce((draft) => {
        draft.order.loads[orderStore.activeTab.index].trackingEnabled = true;
        let req =
          draft.order.loads[orderStore.activeTab.index].loadTrackingRequest;
        if (
          !req ||
          (typeof req === 'object' && Object.keys(req).length === 0)
        ) {
          req = {
            ...defaultLoadTrackingRequest,
          };
        }
        // Always update shipperLink from the latest response
        req.shipperLink = response?.shipperLink as string | null;
        draft.order.loads[orderStore.activeTab.index].loadTrackingRequest = req;
      }),
    );
  }

  return response;
};
